import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ShieldAlert, AlertTriangle, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";

const STATUS_META = {
  expired: { label: "Expiré", cls: "bg-red-100 text-red-800", icon: AlertTriangle },
  expiring_soon: { label: "Expire bientôt", cls: "bg-amber-100 text-amber-800", icon: AlertTriangle },
  incomplete: { label: "Incomplet", cls: "bg-zinc-200 text-zinc-700", icon: ShieldAlert },
};

/**
 * Dashboard widget : Alertes Conformité Sous-traitants (Phase 175).
 * Liste les sous-traitants dont un document expire dans <= X jours
 * (X = subcontractor_dashboard_alert_days config) ou est manquant/expiré,
 * et qui ont compliance_alerts_enabled=true.
 */
export default function SubcontractorComplianceAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [expiryDays, setExpiryDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.getDashboardSubcontractorAlerts().then(({ data }) => {
      setAlerts(data?.alerts || []);
      setExpiryDays(data?.expiry_alert_days || 30);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  return (
    <Card data-testid="dashboard-subcontractor-compliance">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading flex items-center gap-2 text-base sm:text-lg">
          <ShieldAlert className="h-5 w-5 text-amber-600" />
          Alertes Conformité Sous-traitants
          <span className="text-xs font-normal text-muted-foreground">
            (seuil dashboard : {expiryDays} j)
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {alerts.length === 0 ? (
          <p className="text-sm text-muted-foreground italic text-center py-4">
            Aucune alerte. Tous les documents requis sont à jour.
          </p>
        ) : (
          <div className="space-y-2">
            {alerts.map((a) => {
              const meta = STATUS_META[a.status] || STATUS_META.incomplete;
              const Icon = meta.icon;
              const issueCount = (a.missing_doc_type_names?.length || 0)
                + (a.expired_doc_type_ids?.length || 0)
                + (a.expiring_doc_type_ids?.length || 0);
              return (
                <button
                  key={a.id}
                  className="w-full flex items-center gap-3 p-2.5 rounded-md border hover:bg-accent text-left transition-colors"
                  onClick={() => navigate(`/sous-traitants?focus=${a.id}`)}
                  data-testid={`compliance-alert-${a.id}`}
                >
                  <Icon className="h-4 w-4 flex-shrink-0 text-amber-600" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{a.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {a.present_count}/{a.required_count} documents · {issueCount} problème{issueCount > 1 ? "s" : ""}
                      {a.days_until_next_expiry !== null && a.days_until_next_expiry !== undefined && (
                        <> · prochaine expiration dans {a.days_until_next_expiry} j</>
                      )}
                    </p>
                  </div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${meta.cls}`}>{meta.label}</span>
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              );
            })}
          </div>
        )}
        <div className="text-right mt-3">
          <Button size="sm" variant="ghost" className="text-xs" onClick={() => navigate("/sous-traitants")} data-testid="compliance-alert-view-all">
            Voir tous les sous-traitants →
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
